// @flow
import * as React from 'react';
import {Heading, InlineCode, ListItem, List} from 'docs/components';
import Icon from 'sail/Icon';
import {ButtonLink} from 'sail/Button';
import {defineMessages, FormattedMessage} from 'react-intl';
import {renderMarkdocAsReact} from 'docs/markdoc';
import type {RenderNodes} from '@markdoc/markdoc';
import {Table, TableHeaderCell, TableCell} from '../../core/Table/Table';

const messages = defineMessages({
  apiReportType: {
    id: 'docsApp.components.financeReportSchemas.apiReportType',
    description: 'Label for type of API report',
    defaultMessage: 'API report type: <InlineCode>{token}</InlineCode>',
  },
  columnName: {
    id: 'docsApp.components.financeReportSchemas.columnName',
    description: 'Column header for the name of the field',
    defaultMessage: 'Column name',
  },
  default: {
    id: 'docsApp.components.financeReportSchemas.default',
    description:
      'Column header for whether something is a default part of the schema or not',
    defaultMessage: 'Default',
  },
  description: {
    id: 'docsApp.components.financeReportSchemas.description',
    description: 'Column header for the description of the field',
    defaultMessage: 'Description',
  },
  reportType: {
    id: 'docsApp.components.financeReportSchemas.reportType',
    description: 'Column header for type of report',
    defaultMessage: 'Report type',
  },
  requiredRunParams: {
    id: 'docsApp.components.financeReportSchemas.requiredRunParams',
    description: 'Column header for required params',
    defaultMessage: 'Required run parameters',
  },
  optionalRunParams: {
    id: 'docsApp.components.financeReportSchemas.optionalRunParams',
    description: 'Column header for optional params',
    defaultMessage: 'Optional run parameters',
  },
});

const reportHeaderId = (report) => `schema-${report.slug}`;

type Report = {|
  token: string,
  name: string,
  slug: string,
  version: number,
  description: ?string,
  columns: Array<{|
    name: string,
    is_default: boolean,
    description: RenderNodes,
  |}>,
  required_parameters: Array<string>,
  optional_parameters: Array<string>,
|};
type FinanceReportSchemaSectionProps = {|
  report: Report,
|};

const FinanceReportSchemaSection = ({
  report,
}: FinanceReportSchemaSectionProps) => (
  <>
    <Heading level={3} id={reportHeaderId(report)} anchored>
      {report.name}
    </Heading>
    <p>
      <FormattedMessage
        {...messages.apiReportType}
        values={{
          InlineCode: (txt) => <InlineCode>{txt}</InlineCode>,
          token: report.token,
        }}
      />
    </p>
    <Table type="striped">
      <thead>
        <tr>
          <TableHeaderCell width={20}>
            <FormattedMessage {...messages.columnName} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.default} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.description} />
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {report.columns &&
          report.columns.map(({name, is_default, description}) => (
            <tr>
              <TableCell>{name}</TableCell>
              <TableCell>{is_default ? <Icon icon="check" /> : ''}</TableCell>
              <TableCell>{renderMarkdocAsReact(description)}</TableCell>
            </tr>
          ))}
      </tbody>
    </Table>
  </>
);

type ReportsMap = {|
  [string]: Report,
|};

type ReportGroupsMap = {|
  [string]: Array<Report>,
|};

type FinanceReportSchemasProps = {|
  reportsMap: ReportsMap,
  reportGroupsMap: ReportGroupsMap,
  showTableOfContents: boolean,
  keys: Array<string>,
  reportGroupsKeys: Array<string>,
|};

export const FinanceReportSchemas = ({
  reportsMap,
  reportGroupsMap,
  showTableOfContents,
  keys,
  reportGroupsKeys,
}: FinanceReportSchemasProps) => {
  let reports = [];

  if (keys && reportsMap) {
    reports = keys.map((key) => {
      if (!reportsMap[key]) {
        // This key must exist in the Ruby reports context
        // eslint-disable-next-line no-console
        console.error(`No report found for report with key: ${key}`);
      }
      return reportsMap[key];
    });
  } else if (reportGroupsKeys && reportGroupsMap) {
    reports = reportGroupsKeys.flatMap((key) => {
      if (!reportGroupsMap[key]) {
        // This key must exist in the Ruby reports context
        // eslint-disable-next-line no-console
        console.error(`No report group found for report group in map: ${key}`);
      }

      return reportGroupsMap[key];
    });
  }

  const sections = reports.map((report) => {
    return <FinanceReportSchemaSection key={report.token} report={report} />;
  });

  let toc = null;

  if (showTableOfContents) {
    // Note: This should just probably just be added to the actual
    // table of contents
    const contents = keys.map((key) => {
      const report = reportsMap[key];
      return (
        <ListItem key={key}>
          <a href={`#${reportHeaderId(report)}`}>{report.name}</a>
        </ListItem>
      );
    });
    toc = <List>{contents}</List>;
  }

  return (
    <>
      {toc}
      {sections}
    </>
  );
};

type FinanceReportParametersProps = {|
  reportGroupsMap: ReportGroupsMap,
  keys: Array<string>,
|};

export const FinanceReportParameters = ({
  reportGroupsMap,
  keys,
}: FinanceReportParametersProps) => {
  const rows = keys.flatMap((key) => {
    const reports = reportGroupsMap[key];
    return reports.map((report) => {
      return (
        <tr key={report.token}>
          <TableCell className="word-break-cell">
            <InlineCode>{report.token}</InlineCode>
            <ButtonLink
              href={`#${reportHeaderId(report)}`}
              color="blue"
              icon="arrowRight"
              size="small"
              label="Columns"
            />
          </TableCell>
          <TableCell>
            <List>
              {report.required_parameters.map((param) => (
                <ListItem>
                  <InlineCode>{param}</InlineCode>
                </ListItem>
              ))}
            </List>
          </TableCell>
          <TableCell>
            <List>
              {report.optional_parameters.map((param) => (
                <ListItem>
                  <InlineCode>{param}</InlineCode>
                </ListItem>
              ))}
            </List>
          </TableCell>
        </tr>
      );
    });
  });

  return (
    <Table type="striped">
      <thead>
        <tr>
          <TableHeaderCell width={40}>
            <FormattedMessage {...messages.reportType} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.requiredRunParams} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.optionalRunParams} />
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </Table>
  );
};
